import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Avatar,
  Tooltip,
  IconButton,
  Box,
  Button,
  styled,
  InputBase,
  useTheme,
  Typography,
  Chip,
  CircularProgress
} from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import firebase from 'src/utils/firebase';
import { buildApiUrl } from 'src/utils/api';
import AttachFileTwoToneIcon from '@mui/icons-material/AttachFileTwoTone';
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import ImageTwoToneIcon from '@mui/icons-material/ImageTwoTone';
import DescriptionTwoToneIcon from '@mui/icons-material/DescriptionTwoTone';

const MessageInputWrapper = styled(InputBase)(
  ({ theme }) => `
    font-size: ${theme.typography.pxToRem(16)};
    padding: ${theme.spacing(1)};
    width: 100%;
  `
);

const Input = styled('input')({
  display: 'none'
});

function BottomBarContent({
  disabled,
  sending,
  sendError,
  onSend,
  onOptimizeDraft,
  compact = false
}) {
  const { user } = useAuth();
  const theme = useTheme();
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState('');
  const [attachmentError, setAttachmentError] = useState('');

  useEffect(() => {
    if (!disabled) {
      return;
    }

    setMessage('');
    setAttachments([]);
    setOptimizeError('');
    setAttachmentError('');
  }, [disabled]);

  const sendMessage = () => {
    if (disabled || sending || uploadingAttachment || (!message.trim() && !attachments.length)) {
      return;
    }

    onSend({
      message: message.trim(),
      attachments
    });
    setMessage('');
    setAttachments([]);
    setOptimizeError('');
    setAttachmentError('');
  };

  const uploadAttachments = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, 10 - attachments.length);
    event.target.value = '';

    if (!files.length || disabled || sending || uploadingAttachment) {
      return;
    }

    setUploadingAttachment(true);
    setAttachmentError('');
    try {
      const token = await firebase.auth().currentUser?.getIdToken();
      if (!token) {
        throw new Error('Authentication is still loading. Please retry.');
      }

      const uploaded = await Promise.all(files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(buildApiUrl('/api/sms/attachments'), formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        });
        return response.data?.attachment;
      }));

      setAttachments((current) => [
        ...current,
        ...uploaded.filter((attachment) => attachment?.url)
      ].slice(0, 10));
    } catch (error) {
      setAttachmentError(error?.response?.data?.error || error?.message || 'Unable to attach that file.');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const optimizeDraft = async () => {
    if (disabled || sending || optimizing || !message.trim() || typeof onOptimizeDraft !== 'function') {
      return;
    }

    setOptimizing(true);
    setOptimizeError('');
    try {
      const optimized = await onOptimizeDraft(message.trim());
      if (optimized) {
        setMessage(optimized);
      }
    } catch (error) {
      setOptimizeError(error?.message || 'Unable to optimize message.');
    } finally {
      setOptimizing(false);
    }
  };

  const onEnterPress = (evt) => {
    if (evt.key === 'Enter' && !evt.shiftKey) {
      evt.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box sx={{ background: theme.colors.alpha.white[50], p: compact ? 1 : 2 }}>
      <Box display="flex" alignItems="center">
        {!compact && (
          <Avatar sx={{ display: { xs: 'none', sm: 'flex' }, mr: 1 }} alt={user.name} src={user.avatar} />
        )}
        <MessageInputWrapper
          autoFocus
          placeholder={disabled ? 'Select a conversation first...' : 'Write your message here...'}
          fullWidth
          value={message}
          disabled={disabled || sending || optimizing || uploadingAttachment}
          multiline
          maxRows={4}
          onKeyDown={onEnterPress}
          onChange={(evt) => setMessage(evt.target.value)}
          sx={compact ? {
            minHeight: 38,
            px: 1.25,
            py: 0.5,
            mr: 0.5,
            border: `1px solid ${theme.colors.alpha.black[20]}`,
            borderRadius: theme.general.borderRadius,
            fontSize: theme.typography.pxToRem(14)
          } : undefined}
        />

        {!compact && (
          <Tooltip placement="top" title="Choose an emoji">
            <IconButton sx={{ fontSize: theme.typography.pxToRem(16) }} color="primary" disabled>
              <span role="img" aria-label="emoji picker">
                😀
              </span>
            </IconButton>
          </Tooltip>
        )}

        <Tooltip placement="top" title="Attach a file">
          <IconButton sx={{ mx: compact ? 0.25 : 1 }} color="primary" component="label" disabled={disabled || sending || uploadingAttachment || attachments.length >= 10}>
            {uploadingAttachment ? <CircularProgress size={18} /> : <AttachFileTwoToneIcon fontSize="small" />}
            <Input
              accept="image/*,video/*,application/pdf"
              type="file"
              multiple
              onChange={uploadAttachments}
            />
          </IconButton>
        </Tooltip>

        {!compact && (
          <Button
            variant="outlined"
            color="primary"
            sx={{ mr: 1 }}
            disabled={disabled || sending || optimizing || uploadingAttachment || !message.trim()}
            onClick={optimizeDraft}
          >
            {optimizing ? 'Optimizing...' : 'AI Optimize'}
          </Button>
        )}

        {compact ? (
          <Tooltip placement="top" title="Send message">
            <span>
              <IconButton
                color="primary"
                disabled={disabled || sending || optimizing || uploadingAttachment || (!message.trim() && !attachments.length)}
                onClick={sendMessage}
              >
                {sending ? <CircularProgress size={20} /> : <SendTwoToneIcon />}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            startIcon={<SendTwoToneIcon />}
            variant="contained"
            disabled={disabled || sending || optimizing || uploadingAttachment || (!message.trim() && !attachments.length)}
            onClick={sendMessage}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        )}
      </Box>
      {attachments.length > 0 && (
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          {attachments.map((attachment, index) => {
            const contentType = String(attachment.content_type || attachment.contentType || '').toLowerCase();
            return (
              <Chip
                key={attachment.id || attachment.url || index}
                icon={contentType.startsWith('image/') ? <ImageTwoToneIcon /> : <DescriptionTwoToneIcon />}
                label={attachment.filename || `Attachment ${index + 1}`}
                onDelete={() => setAttachments((current) => current.filter((item) => item.url !== attachment.url))}
                size="small"
                variant="outlined"
              />
            );
          })}
        </Box>
      )}
      {attachmentError && (
        <Typography variant="subtitle2" color="error" sx={{ mt: 1 }}>
          {attachmentError}
        </Typography>
      )}
      {optimizeError && (
        <Typography variant="subtitle2" color="error" sx={{ mt: 1 }}>
          {optimizeError}
        </Typography>
      )}
      {sendError && (
        <Typography variant="subtitle2" color="error" sx={{ mt: 1 }}>
          {sendError}
        </Typography>
      )}
    </Box>
  );
}

export default BottomBarContent;
